import { useTracking } from '@eventbrite/ads';
import { Carousel } from '@eventbrite/eds-carousel';
import { isEmpty } from 'lodash';
import React, { useContext, useRef } from 'react';
import { TrackingContext } from '../../../contexts';
import { aggregateContext } from '../tracking';
import { OtherEventsYouMayLikeContentProps } from '../types';
import { getEventCardsMap } from './getEventCardsMap';
import styles from './OtherEventsYouMayLike.module.scss';
import { useOtherEventsYouMayLike } from './OtherEventsYouMayLikeData';

const DESKTOP_SLIDE_SIZE = 3;
const MOBILE_SLIDE_SIZE = 1;
const COMMON_CAROUSEL_PROPS = {
    hasTopControls: true,
    isNotContinuous: true,
    alwaysShowSideNavigation: true,
    usesNewStyle: true,
    fullWidth: true,
};

export function OtherEventsYouMayLikeContent({
    isAuthenticated,
    areMetricsEnabled,
    shareComponentProps,
    eventId,
    eventsToRemove,
    locale,
    bucketLabel,
}: OtherEventsYouMayLikeContentProps) {
    const currentPage = useRef({ number: 0, size: 0 });
    const context = useContext(TrackingContext);
    const sortedEvents = useOtherEventsYouMayLike({
        eventId,
        isAuthenticated,
        suspense: true,
        eventsToRemove,
    });

    const { ref } = useTracking<HTMLDivElement>(({ id, event }) =>
        aggregateContext({
            id,
            event: { ...event, id },
            events: sortedEvents,
            context,
            page: currentPage.current,
        }),
    );

    const eventCards = getEventCardsMap({
        events: sortedEvents,
        isAuthenticated,
        shareComponentProps,
        areMetricsEnabled,
        locale,
        bucketLabel,
        trackingLabel: '',
        showToggleChips: false,
    });

    const wrapEventCardMapForDesktop = () => {
        const slides = [];

        for (let i = 0; i < eventCards.length; i += DESKTOP_SLIDE_SIZE) {
            const slideItem = eventCards.filter(
                (_, index) => index >= i && index < i + DESKTOP_SLIDE_SIZE,
            );
            slides.push(slideItem);
        }
        return slides;
    };

    const eventCardMapForDesktop = wrapEventCardMapForDesktop().map(
        (slide, index) => {
            return (
                <div className={styles.carouselDesktop} key={index}>
                    {slide}
                </div>
            );
        },
    );

    if (isEmpty(sortedEvents)) {
        return null;
    }

    return (
        <section
            className={styles.relatedEvents}
            ref={ref}
            style={{ marginTop: -48 }}
        >
            <div className="eds-show-up-mn" data-testid="carousel-desktop">
                <Carousel
                    onTransition={(toPage: number) => {
                        currentPage.current = {
                            number: toPage,
                            size: DESKTOP_SLIDE_SIZE,
                        };
                    }}
                    {...COMMON_CAROUSEL_PROPS}
                >
                    {eventCardMapForDesktop}
                </Carousel>
            </div>

            <div
                className="eds-show-down-sw carousel-mobile"
                data-testid="carousel-mobile"
            >
                <Carousel
                    onTransition={(toPage: number) => {
                        currentPage.current = {
                            number: toPage,
                            size: MOBILE_SLIDE_SIZE,
                        };
                    }}
                    {...COMMON_CAROUSEL_PROPS}
                >
                    {eventCards}
                </Carousel>
            </div>
        </section>
    );
}

export default OtherEventsYouMayLikeContent;
